'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import LobbyGamesSlider from './components/LobbyGamesSlider';
import { Category } from '@/api/games';
import { ImageComponent } from '@/components/Image';
import LiveIndicator from '@/components/LiveIndicator';
import { RecentGames } from '@/components/RecentGames';
import { Skeleton } from '@/components/Skeleton';
import {
  CategoriesSliderSkeleton,
  PaymentsSliderSkeleton,
  ProvidersSliderSkeleton,
} from '@/components/skeletons';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { getGameCategoriesTransMap } from '@/helpers/game';
import {
  legendsSliderParams,
  oneRowSliderLiveCasinoParams,
  oneRowSliderParams,
  sliderTopCategoryParams,
  twoRowsSliderParams,
} from '@/helpers/games';
import { isFrenchLocale, Locale } from '@/helpers/languages';
import { MAP_LEGENDS_CATEGORY_BY_LOCALE, useABLegendsLobby } from '@/hooks';
import { useGameList } from '@/hooks/gameLobbbyList';
import { useNavigation } from '@/hooks/navigation';
import { useCommonStore } from '@/store';

import { ScContainer, ScLobbySliderWrapper, Spinner } from '@/styles/common';

export const LobbyPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');

  const { mainColor, blackColor } = useTheme();

  const { generateLink } = useNavigation();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  // const isLiveSpinsAvailable = useCommonStore.use.isLiveSpinsAvailable();

  const getSliderTitle = (category: Category) => {
    return `${getGameCategoriesTransMap(category, t)}${
      isFrenchLocale(language) ? '' : ` ${t('game.games')}`
    }`;
  };

  const { exclusiveGamesList, exclusiveGamesLength } = useGameList();

  const PaymentsSlider = dynamic(() => import('./components/PaymentsSlider'), {
    loading: () => <PaymentsSliderSkeleton />,
    ssr: false,
  });

  const ProvidersSlider = dynamic(
    () => import('./components/ProvidersSlider'),
    {
      loading: () => <ProvidersSliderSkeleton />,
      ssr: false,
    }
  );

  const CategoriesSlider = dynamic(
    () => import('./components/CategoriesSlider'),
    {
      loading: () => <CategoriesSliderSkeleton />,
      ssr: false,
    }
  );

  const StarWinners = dynamic(() => import('./components/StarWinners'), {
    loading: () => (
      <Spinner size={50} color={mainColor} background={blackColor} />
    ),
    ssr: false,
  });

  const JackpotCategory = dynamic(
    () => import('@/components/JackpotCategory'),
    {
      loading: () => <GamesSkeleton withTitle />,
      ssr: false,
    }
  );

  const TopicsSlider = dynamic(() => import('./components/TopicsSlider'), {
    loading: () => <ProvidersSliderSkeleton />,
    ssr: false,
  });

  const RandomizeSlider = dynamic(
    () => import('@/components/RandomizeSlider'),
    {
      loading: () => (
        <Spinner size={50} color={mainColor} background={blackColor} />
      ),
      ssr: false,
    }
  );

  const MobileAppPromoBanner = dynamic(
    () => import('@/components/MobileAppPromoBanner'),
    {
      loading: () => <Skeleton.Rect height="120" />,
      ssr: false,
    }
  );

  const JackpotBanner = dynamic(() => import('@/components/JackpotBanner'), {
    loading: () => <Skeleton.Rect height="128" />,
    ssr: false,
  });

  const PromotionsCarousel = dynamic(
    () => import('./components/PromotionsCarousel'),
    {
      loading: () => <GamesSkeleton withTitle />,
      ssr: false,
    }
  );

  // const LiveSpins = dynamic(() => import('./components/LiveSpins'), {
  //   loading: () => <GamesSkeleton withTitle />,
  //   ssr: false,
  // });

  const isLegendsVisible = useABLegendsLobby();

  return (
    <>
      <ScContainer>
        <ScLobbySliderWrapper>
          {isAuthenticated && <RecentGames />}
          {isLegendsVisible && (
            <LobbyGamesSlider
              categoryId={MAP_LEGENDS_CATEGORY_BY_LOCALE[language as Locale]}
              title={getGameCategoriesTransMap(Category.LEGENDS_EN, t)}
              sliderParams={legendsSliderParams}
            />
          )}
          <LobbyGamesSlider
            categoryId={Category.EXCLUSIVE}
            title={getGameCategoriesTransMap(Category.EXCLUSIVE, t)}
            sliderParams={oneRowSliderLiveCasinoParams}
            gamesList={exclusiveGamesList}
            gamesLength={exclusiveGamesLength}
          />
          <LobbyGamesSlider
            categoryId={Category.TOP}
            title={getSliderTitle(Category.TOP)}
            titleIcon={
              <ImageComponent
                width={24}
                height={24}
                src="/topIcon.webp"
                alt="icon"
              />
            }
            sliderParams={sliderTopCategoryParams}
          />

          {/* {isLiveSpinsAvailable && <LiveSpins />} */}

          {/* only for testing purposes */}
          <Link
            id="testDemoGame"
            href={generateLink('/game/slotopia/level-up-mega-fruit?demo=true')}
            style={{ display: 'none' }}
          />
          {!isAuthenticated && <PromotionsCarousel />}
          <LobbyGamesSlider
            categoryId={Category.NEW}
            title={getSliderTitle(Category.NEW)}
            sliderParams={twoRowsSliderParams}
          />

          <LobbyGamesSlider
            categoryId={Category.CASUAL}
            titleIcon={<>⚡</>}
            title={getGameCategoriesTransMap(Category.INSTANT_WINS, t)}
            sliderParams={oneRowSliderParams}
          />

          <JackpotBanner />

          <LobbyGamesSlider
            categoryId={Category.BONUS_BUY}
            title={getGameCategoriesTransMap(Category.BONUS_BUY, t)}
            sliderParams={oneRowSliderParams}
          />
          <JackpotCategory />
          {/* <LobbyGamesSlider
            categoryId={Category.TABLES}
            title={getGameCategoriesTransMap(Category.TABLES, t)}
            sliderParams={sliderParams}
          /> */}
        </ScLobbySliderWrapper>
      </ScContainer>

      <RandomizeSlider />

      <ScContainer>
        <ScLobbySliderWrapper>
          <LobbyGamesSlider
            titleIcon={<LiveIndicator />}
            categoryId={Category.LIVE}
            title={`${getGameCategoriesTransMap(Category.LIVE, t)}${t('casino')}`}
            sliderParams={oneRowSliderLiveCasinoParams}
          />
          <TopicsSlider />
          <StarWinners />
          <MobileAppPromoBanner />
          <CategoriesSlider />
          <ProvidersSlider />
          <PaymentsSlider />
        </ScLobbySliderWrapper>
      </ScContainer>
    </>
  );
};
